/**
 * One live connection for every Velora window (QA r57).
 *
 * Each window used to open its own `GET /v1/events` stream. Over HTTP/1.1
 * (local and LAN stacks) a browser allows 6 connections per host, so the
 * main window plus 5 pop-outs used them all up: the main window froze and
 * further pop-outs never loaded.
 *
 * Now the windows of one browser profile (or one desktop app session, whose
 * windows share a partition) elect a leader with the Web Locks API. Only the
 * leader holds EventSources, one per office selector value some window asks
 * for (normally just one), and relays every event, status and resync to the
 * others over a BroadcastChannel. When the leader window closes its lock is
 * released, the next window takes over, opens the stream and tells everyone
 * to refetch (they may have missed events in between).
 *
 * Without BroadcastChannel or Web Locks the caller falls back to one
 * {@link LiveClient} per window, as before.
 *
 * Events carry ids only, never PHI, and the channel never leaves the origin.
 */
import type { EntityChanged } from '@velora/contracts';
import { LiveClient, type LiveClientOptions, type LiveStatus } from './client.js';

export const LIVE_CHANNEL = 'velora-live-v1';
export const LIVE_LOCK = 'velora-live-leader';
/** How often a window repeats which office it wants. */
export const WANT_EVERY_MS = 5_000;
/**
 * A window not heard from for this long no longer keeps its office's stream.
 * Generous on purpose: Chrome runs a hidden page's timers about once a
 * minute, and a window that closes normally says `bye` anyway.
 */
export const WANT_STALE_MS = 90_000;

export type LiveMessage =
  | { t: 'want'; id: string; office: string }
  | { t: 'bye'; id: string }
  | { t: 'hello' }
  | { t: 'event'; office: string; event: EntityChanged }
  | { t: 'resync'; office: string }
  | { t: 'status'; office: string; status: LiveStatus };

export interface ChannelLike {
  postMessage(msg: LiveMessage): void;
  onmessage: ((ev: { data: unknown }) => void) | null;
  close(): void;
}

export interface LocksLike {
  request(name: string, cb: () => Promise<void>): Promise<unknown>;
}

/** The surface LiveProvider uses; LiveClient and SharedLiveClient both have it. */
export interface LiveConnection {
  readonly status: LiveStatus;
  connect(office: string): void;
  close(): void;
}

export interface SharedLiveOptions extends Pick<
  LiveClientOptions,
  'onChange' | 'onResync' | 'onStatus'
> {
  channel: ChannelLike;
  locks: LocksLike;
  /** Builds the leader's per-office stream; default a real LiveClient. */
  createClient?: (o: LiveClientOptions) => LiveConnection;
  now?: () => number;
  setInterval?: (fn: () => void, ms: number) => unknown;
  clearInterval?: (h: unknown) => void;
  id?: string;
}

interface Stream {
  client: LiveConnection;
  status: LiveStatus;
  everOpened: boolean;
}

export class SharedLiveClient implements LiveConnection {
  private readonly id: string;
  private office: string | null = null;
  private statusValue: LiveStatus = 'idle';
  private leader = false;
  private closed = false;
  /** Set once another window was leader: our first open must resync everyone. */
  private sawOtherLeader = false;
  private releaseLock: (() => void) | null = null;
  private lockRequested = false;
  private heartbeat: unknown = null;
  /** Leader only: which window wants which office, and when we last heard. */
  private readonly wants = new Map<string, { office: string; at: number }>();
  private readonly streams = new Map<string, Stream>();
  private readonly opts: Required<
    Omit<SharedLiveOptions, 'onStatus' | 'id' | 'channel' | 'locks'>
  > &
    Pick<SharedLiveOptions, 'onStatus' | 'channel' | 'locks'>;

  constructor(options: SharedLiveOptions) {
    this.id = options.id ?? randomId();
    this.opts = {
      createClient: (o) => new LiveClient(o),
      now: () => Date.now(),
      setInterval: (fn, ms) => setInterval(fn, ms),
      clearInterval: (h) => clearInterval(h as ReturnType<typeof setInterval>),
      ...options,
    };
    this.opts.channel.onmessage = (ev) => this.onMessage(ev.data as LiveMessage);
  }

  get status(): LiveStatus {
    return this.statusValue;
  }

  /** True while this window holds the stream for everyone. */
  get isLeader(): boolean {
    return this.leader;
  }

  connect(office: string): void {
    if (this.closed) return;
    if (this.office === office) return;
    this.office = office;
    this.setStatus('connecting');
    this.announce();
    this.heartbeat ??= this.opts.setInterval(() => {
      this.announce();
      if (this.leader) this.reconcile();
    }, WANT_EVERY_MS);
    if (this.leader) {
      this.reconcile();
      const s = this.streams.get(office);
      if (s) this.setStatus(s.status);
    }
    if (!this.lockRequested) {
      this.lockRequested = true;
      void this.opts.locks
        .request(
          LIVE_LOCK,
          () =>
            new Promise<void>((resolve) => {
              if (this.closed) {
                resolve();
                return;
              }
              this.releaseLock = resolve;
              this.becomeLeader();
            }),
        )
        .catch(() => undefined);
    }
  }

  close(): void {
    if (this.closed) return;
    this.closed = true;
    this.post({ t: 'bye', id: this.id });
    if (this.heartbeat !== null) this.opts.clearInterval(this.heartbeat);
    this.heartbeat = null;
    for (const s of this.streams.values()) s.client.close();
    this.streams.clear();
    this.leader = false;
    this.releaseLock?.();
    this.releaseLock = null;
    this.opts.channel.onmessage = null;
    this.opts.channel.close();
    this.office = null;
    this.setStatus('closed');
  }

  private becomeLeader(): void {
    this.leader = true;
    if (this.office !== null) this.wants.set(this.id, { office: this.office, at: this.opts.now() });
    // Ask every other window which office it wants; they answer with `want`.
    this.post({ t: 'hello' });
    this.reconcile();
  }

  /** Leader: one stream per office somebody still wants, none for the rest. */
  private reconcile(): void {
    if (!this.leader) return;
    const now = this.opts.now();
    if (this.office !== null) this.wants.set(this.id, { office: this.office, at: now });
    for (const [id, w] of this.wants) if (now - w.at > WANT_STALE_MS) this.wants.delete(id);
    const needed = new Set([...this.wants.values()].map((w) => w.office));
    for (const [office, s] of this.streams) {
      if (!needed.has(office)) {
        s.client.close();
        this.streams.delete(office);
      }
    }
    for (const office of needed) if (!this.streams.has(office)) this.openStream(office);
  }

  private openStream(office: string): void {
    const stream: Stream = {
      status: 'connecting',
      everOpened: false,
      client: null as unknown as LiveConnection,
    };
    stream.client = this.opts.createClient({
      onChange: (event) => {
        this.post({ t: 'event', office, event });
        if (office === this.office) this.opts.onChange(event);
      },
      onResync: () => this.resync(office),
      onStatus: (status) => {
        stream.status = status;
        if (status === 'open' && !stream.everOpened) {
          stream.everOpened = true;
          // A takeover: the old leader's stream is gone, events may be lost.
          if (this.sawOtherLeader) this.resync(office);
        }
        this.post({ t: 'status', office, status });
        if (office === this.office) this.setStatus(status);
      },
    });
    this.streams.set(office, stream);
    stream.client.connect(office);
  }

  private resync(office: string): void {
    this.post({ t: 'resync', office });
    if (office === this.office) this.opts.onResync();
  }

  private onMessage(m: LiveMessage | null | undefined): void {
    if (this.closed || !m || typeof m !== 'object') return;
    switch (m.t) {
      case 'hello':
        if (!this.leader) this.sawOtherLeader = true;
        this.announce();
        return;
      case 'want':
        if (!this.leader) return;
        this.wants.set(m.id, { office: m.office, at: this.opts.now() });
        this.reconcile();
        {
          // Tell the newcomer where its office's stream stands.
          const s = this.streams.get(m.office);
          if (s) this.post({ t: 'status', office: m.office, status: s.status });
        }
        return;
      case 'bye':
        if (!this.leader) return;
        this.wants.delete(m.id);
        this.reconcile();
        return;
      case 'event':
        if (this.leader) return;
        this.sawOtherLeader = true;
        if (m.office === this.office) this.opts.onChange(m.event);
        return;
      case 'resync':
        if (this.leader) return;
        if (m.office === this.office) this.opts.onResync();
        return;
      case 'status':
        if (this.leader) return;
        this.sawOtherLeader = true;
        if (m.office === this.office) this.setStatus(m.status);
        return;
      default:
        return;
    }
  }

  private announce(): void {
    if (this.closed || this.office === null) return;
    if (this.leader) this.wants.set(this.id, { office: this.office, at: this.opts.now() });
    else this.post({ t: 'want', id: this.id, office: this.office });
  }

  private post(m: LiveMessage): void {
    try {
      this.opts.channel.postMessage(m);
    } catch {
      // A closed channel during unload; nothing to tell.
    }
  }

  private setStatus(s: LiveStatus): void {
    if (s === this.statusValue) return;
    this.statusValue = s;
    this.opts.onStatus?.(s);
  }
}

function randomId(): string {
  const c = globalThis.crypto as Crypto | undefined;
  if (c?.randomUUID) return c.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

/**
 * The live connection for this window: shared across windows when the
 * browser can (BroadcastChannel + Web Locks), else its own stream.
 */
export function createLiveConnection(
  o: Pick<LiveClientOptions, 'onChange' | 'onResync' | 'onStatus'>,
): LiveConnection {
  const locks = (globalThis.navigator as { locks?: LocksLike } | undefined)?.locks;
  if (typeof BroadcastChannel !== 'undefined' && locks && typeof locks.request === 'function') {
    try {
      const channel = new BroadcastChannel(LIVE_CHANNEL) as unknown as ChannelLike;
      return new SharedLiveClient({ ...o, channel, locks });
    } catch {
      // Fall through to a stream of our own.
    }
  }
  return new LiveClient(o);
}
